import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { BarChart2, Users, Clock, TrendingUp, ArrowUp, Monitor, Smartphone, Globe } from 'lucide-react';
import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import { useSessionsList } from '@/hooks/sessions/useSessionsList';
import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { DashboardMetricCard } from '@/components/dashboard/DashboardMetricCard';
import { QuickSuggestionPills } from '@/components/dashboard/QuickSuggestionPills';
import { DemosChart, type DemoChartDataPoint } from '@/components/dashboard/DemosChart';
import { DemosTable, type DemoTableRow } from '@/components/dashboard/DemosTable';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { PageHeader } from '@/components/layout/PageHeader';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.round(seconds / 60);
  return `${minutes}m`;
};

// Chart colors — derived from CSS vars at render time for theme support
const DEVICE_COLORS = [
  'hsl(var(--foreground))',          // desktop — primary (near-black / near-white)
  'hsl(var(--muted-foreground))',    // mobile — muted
];

const SOURCE_COLORS = [
  'hsl(var(--foreground))',          // primary source
  'hsl(var(--muted-foreground))',    // secondary
  'hsl(var(--border))',              // tertiary
  'hsl(var(--muted-foreground) / 0.5)', // quaternary
];

const MiniDonut: React.FC<{
  segments: { value: number; color: string }[];
  size?: number;
  strokeWidth?: number;
  centerLabel?: string;
}> = ({ segments, size = 80, strokeWidth = 10, centerLabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;

  let accumulated = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
      <circle
        cx={center} cy={center} r={radius}
        fill="none" strokeWidth={strokeWidth}
        stroke="hsl(var(--muted))"
      />
      {segments.map((seg, i) => {
        const segLen = (seg.value / total) * circumference;
        const offset = accumulated;
        accumulated += segLen;
        if (seg.value === 0) return null;
        return (
          <circle
            key={i}
            cx={center} cy={center} r={radius}
            fill="none" strokeWidth={strokeWidth}
            stroke={seg.color}
            strokeDasharray={`${segLen} ${circumference - segLen}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${center} ${center})`}
            style={{ transition: 'stroke-dasharray 0.3s ease' }}
          />
        );
      })}
      {centerLabel && (
        <text
          x={center} y={center}
          textAnchor="middle" dominantBaseline="central"
          fill="hsl(var(--muted-foreground))"
          fontSize="11" fontWeight="500"
        >
          {centerLabel}
        </text>
      )}
    </svg>
  );
};

const DashboardPage: React.FC = () => {
  const { userData } = useAuth();
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [thinkingEnabled, setThinkingEnabled] = useState(false);

  // Fetch sessions for chart and table
  const { sessions, isLoading: isLoadingSessions } = useSessionsList(
    currentOrganization?.id,
    { limit: 100 }
  );

  // Fetch dashboard metrics from the new endpoint
  const { data: dashboardMetrics, isLoading: isLoadingMetrics } = useDashboardMetrics(
    currentOrganization?.id,
    { days: 30 }
  );

  const firstName = useMemo(() => {
    const displayName = userData?.displayName?.trim();
    if (displayName) {
      const name = displayName.split(' ')[0] || 'there';
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    const email = userData?.email;
    if (email) {
      const localPart = email.split('@')[0];
      const name = localPart || 'there';
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return 'There';
  }, [userData?.displayName, userData?.email]);

  // Generate chart data by aggregating sessions by date
  const chartData: DemoChartDataPoint[] = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];

    const dateMap = new Map<string, { total: number; qualified: number; booked: number; notBooked: number }>();

    sessions.forEach(session => {
      const dateStr = session.created_at
        ? new Date(session.created_at).toISOString().split('T')[0]
        : null;
      if (!dateStr) return;

      const existing = dateMap.get(dateStr) || { total: 0, qualified: 0, booked: 0, notBooked: 0 };
      existing.total += 1;
      
      const qualStatus = session.qualification_status?.toLowerCase();
      const bookStatus = session.booked_status?.toLowerCase();

      if (qualStatus === 'qualified' || bookStatus === 'booked') {
        existing.qualified += 1;
      }

      if (bookStatus === 'booked') {
        existing.booked += 1;
      } else {
        existing.notBooked += 1;
      }
      dateMap.set(dateStr, existing);
    });

    return Array.from(dateMap.entries())
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [sessions]);

  // Transform sessions to table rows (sorted by most recent first)
  const tableData: DemoTableRow[] = useMemo(() => {
    if (!sessions) return [];

    return sessions
      .map(session => ({
        id: session.session_recording_id,
        email: session.email || 'Anonymous',
        qualification_status: session.qualification_status,
        booked_status: session.booked_status,
        role: session.decision_maker_role,
        date: session.created_at,
        duration: session.duration_seconds,
      }))
      .sort((a, b) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });
  }, [sessions]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && prompt.trim()) {
      e.preventDefault();
      navigate(`/ask-hobbes?prompt=${encodeURIComponent(prompt.trim())}`);
    }
  };

  const handleSubmit = () => {
    if (prompt.trim()) {
      navigate(`/ask-hobbes?prompt=${encodeURIComponent(prompt.trim())}`);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    navigate(`/ask-hobbes?prompt=${encodeURIComponent(suggestion)}`);
  };

  // Pre-compute device breakdown
  const deviceBreakdown = useMemo(() => {
    const desktop = dashboardMetrics?.desktopCount ?? 0;
    const mobile = dashboardMetrics?.mobileCount ?? 0;
    const total = desktop + mobile || 1;
    return {
      desktop,
      mobile,
      total,
      desktopPct: Math.round((desktop / total) * 100),
      mobilePct: Math.round((mobile / total) * 100),
    };
  }, [dashboardMetrics?.desktopCount, dashboardMetrics?.mobileCount]);

  // Pre-compute source breakdown
  const sourceBreakdown = useMemo(() => {
    const sources = dashboardMetrics?.topSources ?? [];
    const maxCount = sources.length > 0 ? sources[0].count : 1;
    return { sources, maxCount };
  }, [dashboardMetrics?.topSources]);

  const isLoading = isLoadingSessions || isLoadingMetrics;

  if (!currentOrganization?.id) {
    return <div className="p-4 text-center">Select an organization to view the dashboard.</div>;
  }

  return (
    <>
      <Helmet>
        <title>Home | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
      <PageHeader title="Home" />

      {/* Scrollable Content */}
      <div className="flex-1 min-w-0 px-4 sm:px-6 md:px-8 py-6">
        {/* Greeting */}
        <h1 className="text-2xl font-semibold text-foreground mb-6">
          {getGreeting()}, {firstName}!
        </h1>

        {/* Ask Hobbes Section */}
        <div className="mb-8">
          {/* Ask Hobbes Input Card */}
          <div className="card-bg-light border border-[hsl(var(--input))] rounded-[12px] mb-4">
            <div className="px-4 pt-4 pb-2">
                <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Hobbes anything..."
                className="w-full text-sm text-foreground placeholder:opacity-100 placeholder:text-muted-foreground bg-transparent border-none outline-none resize-none min-h-[60px]"
                rows={2}
              />
            </div>
            <div className="px-4 pb-3 flex items-center justify-between border-t border-default pt-3">
              <div className="flex items-center gap-2">
                <Switch
                  checked={thinkingEnabled}
                  onCheckedChange={setThinkingEnabled}
                  className="data-[state=checked]:bg-primary"
                />
                <span className="text-sm text-muted-foreground">Thinking</span>
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!prompt.trim()}
                className="bg-primary hover:bg-primary/90 disabled:bg-border disabled:cursor-not-allowed text-white rounded-full transition-colors h-[30px] w-[30px] flex items-center justify-center"
              >
                <ArrowUp size={16} />
              </button>
            </div>
          </div>
          <QuickSuggestionPills onSuggestionClick={handleSuggestionClick} />
        </div>

        {/* Overview Section */}
        <div className="mb-8 @container">
          <h2 className="text-lg font-semibold text-foreground mb-4">Overview</h2>
          {isLoading ? (
            <div className="grid grid-cols-1 @lg:grid-cols-2 @3xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white dark:bg-zinc-900 rounded-xl border border-border dark:border-zinc-700 p-5">
                  <Skeleton className="h-4 w-24 mb-4" />
                  <Skeleton className="h-8 w-16 mb-2" />
                  <Skeleton className="h-8 w-20" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 @lg:grid-cols-2 @3xl:grid-cols-4 gap-4">
              <DashboardMetricCard
                title="Total Demos"
                value={(dashboardMetrics?.totalDemos ?? 0).toLocaleString()}
                dateLabel="Last 30 days"
                icon={BarChart2}
                iconBgColor="bg-blue-100 dark:bg-blue-900/40"
                sparklineData={dashboardMetrics?.timeSeries?.totalSessions?.map(d => ({ date: d.date, value: d.value })) || []}
              />
              <DashboardMetricCard
                title="Qualification Rate"
                value={`${dashboardMetrics?.qualificationRate ?? 0}%`}
                dateLabel="Last 30 days"
                subtitle={`${dashboardMetrics?.qualifiedCount ?? 0} qualified, ${dashboardMetrics?.unqualifiedCount ?? 0} unqualified`}
                icon={TrendingUp}
                iconBgColor="bg-green-100 dark:bg-green-900/40"
                sparklineData={dashboardMetrics?.timeSeries?.qualificationRate?.map(d => ({ date: d.date, value: d.value })) || []}
              />
              <DashboardMetricCard
                title="Avg Session Length"
                value={formatDuration(dashboardMetrics?.avgSessionLengthSeconds ?? 0)}
                dateLabel="Last 30 days"
                icon={Clock}
                iconBgColor="bg-purple-100 dark:bg-purple-900/40"
                sparklineData={dashboardMetrics?.timeSeries?.avgDuration?.map(d => ({ date: d.date, value: d.value / 60 })) || []}
              />
              <DashboardMetricCard
                title="High-Intent Leads"
                value={(dashboardMetrics?.highIntentLeads ?? 0).toLocaleString()}
                dateLabel="Last 30 days"
                icon={Users}
                iconBgColor="bg-orange-100 dark:bg-orange-900/40"
                sparklineData={dashboardMetrics?.timeSeries?.highIntentLeads?.map(d => ({ date: d.date, value: d.value })) || []}
              />
            </div>
          )}
        </div>

        {/* Breakdown Cards */}
        {!isLoading && dashboardMetrics && (
          <div className="@container mb-8">
          <div className="grid grid-cols-1 @lg:grid-cols-2 gap-4">
            {/* Device Breakdown */}
            <Card className="card-bg card-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/40">
                    <Monitor className="h-4 w-4 text-foreground" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground">Device Breakdown</span>
                    <span className="text-xs text-muted-foreground">Last 30 days</span>
                  </div>
                </div>
                <div className="flex items-center gap-5 mt-4">
                  <MiniDonut
                    centerLabel={deviceBreakdown.total > 1 ? deviceBreakdown.total.toLocaleString() : undefined}
                    segments={[
                      { value: deviceBreakdown.desktop, color: DEVICE_COLORS[0] },
                      { value: deviceBreakdown.mobile, color: DEVICE_COLORS[1] },
                    ]}
                  />
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: DEVICE_COLORS[0] }} />
                        <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm text-foreground">Desktop</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{deviceBreakdown.desktop.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">{deviceBreakdown.desktopPct}%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: DEVICE_COLORS[1] }} />
                        <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm text-foreground">Mobile</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{deviceBreakdown.mobile.toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">{deviceBreakdown.mobilePct}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Source Breakdown */}
            <Card className="card-bg card-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-teal-100 dark:bg-teal-900/40">
                    <Globe className="h-4 w-4 text-foreground" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted-foreground">Top Sources</span>
                    <span className="text-xs text-muted-foreground">Last 30 days</span>
                  </div>
                </div>
                {sourceBreakdown.sources.length === 0 ? (
                  <p className="text-sm text-muted-foreground mt-4">No sources yet.</p>
                ) : (
                  <div className="flex items-center gap-5 mt-4">
                    <MiniDonut
                      segments={sourceBreakdown.sources.map((src, i) => ({
                        value: src.count,
                        color: SOURCE_COLORS[i] || SOURCE_COLORS[SOURCE_COLORS.length - 1],
                      }))}
                    />
                    <div className="flex-1 space-y-3">
                      {sourceBreakdown.sources.map((src, i) => (
                        <div key={src.source} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: SOURCE_COLORS[i] || SOURCE_COLORS[SOURCE_COLORS.length - 1] }}
                            />
                            <span className="text-sm text-foreground capitalize">{src.source}</span>
                          </div>
                          <span className="text-sm font-medium text-foreground">{src.count.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          </div>
        )}

        {/* Chart Section */}
        <div className="mb-8">
          <DemosChart data={chartData} />
        </div>

        {/* Table Section */}
        <DemosTable demos={tableData} isLoading={isLoadingSessions} />
      </div>
    </div>
    </>
  );
};

export default DashboardPage;
